import React, { useEffect, useState } from "react";
import { formatFileSize, compressImage, totalSize, isOverLimit, SAFE_TOTAL_BYTES } from "../utils/fileUpload";
import "../styles/formPage.css";

/**
 * 새로 추가할 파일 선택 + 미리보기 + 개별 제거.
 * image=true 면 썸네일 그리드, 아니면 파일명 리스트로 표시한다.
 * files/onChange 로 상태를 상위(폼)에서 관리한다.
 */
export default function FileUploader({ id, label, hint, accept, image = false, files, onChange, disabled }) {
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    if (!image) { setPreviews([]); return undefined; }
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files, image]);

  const [busy, setBusy] = useState(false);

  // 이미지는 업로드 전에 브라우저에서 리사이즈·재압축한다.
  // 서버 앞단 nginx 한도(1MB)를 넘으면 백엔드에 닿지도 못하고 413 이 나기 때문.
  const addFiles = async (fileList) => {
    const picked = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (picked.length === 0) return;
    if (!image) { onChange([...files, ...picked]); return; }
    setBusy(true);
    try {
      const processed = await Promise.all(picked.map((f) => compressImage(f)));
      onChange([...files, ...processed]);
    } finally {
      setBusy(false);
    }
  };
  const removeAt = (index) => onChange(files.filter((_, i) => i !== index));

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      <label className="uploader__drop" htmlFor={id}>
        <span className="uploader__drop-main">클릭하여 파일 선택</span>
        {hint && <span className="uploader__drop-hint">{hint}</span>}
        {busy && <span className="uploader__drop-hint">이미지 최적화 중…</span>}
      </label>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple
        hidden
        disabled={disabled || busy}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      {image && files.length > 0 && (
        <p className={`uploader__total${isOverLimit(files) ? " is-over" : ""}`}>
          총 {formatFileSize(totalSize(files))}
          {isOverLimit(files) && ` — 서버 한도(${formatFileSize(SAFE_TOTAL_BYTES)})를 넘어 업로드가 실패할 수 있습니다`}
        </p>
      )}

      {files.length > 0 && (
        <ul className={`uploader__list${image ? " uploader__list--grid" : ""}`}>
          {files.map((file, i) => (
            <li key={`${file.name}-${i}`} className="uploader__item">
              {image ? (
                <img className="uploader__thumb" src={previews[i]} alt={file.name} />
              ) : (
                <span className="uploader__name">{file.name}</span>
              )}
              <span className="uploader__size">{formatFileSize(file.size)}</span>
              <button
                type="button"
                className="uploader__remove"
                aria-label="제거"
                onClick={() => removeAt(i)}
                disabled={disabled}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 수정 폼용: 기존 파일 목록을 유지/삭제 토글로 관리.
 * deletedIds(Set)에 담긴 id 는 삭제 표시. resolveUrl 로 미리보기 URL 을 만든다.
 */
export function ExistingFiles({ label, items, deletedIds, onToggle, image = false, resolveUrl }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <ul className={`uploader__list${image ? " uploader__list--grid" : ""}`}>
        {items.map((it, i) => {
          const marked = deletedIds.has(it.id);
          const name = it.originalFileName || it.filePath || it.url || "";
          return (
            <li key={it.id ?? i} className={`uploader__item${marked ? " is-deleted" : ""}`}>
              {image ? (
                <img
                  className="uploader__thumb"
                  src={resolveUrl(it.url ?? it.imagePath)}
                  alt={name}
                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                />
              ) : (
                <span className="uploader__name">{name}</span>
              )}
              <button type="button" className="uploader__remove uploader__remove--wide" onClick={() => onToggle(it.id)}>
                {marked ? "복구" : "삭제"}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
